import { ApolloError, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  ComplianceProgramAppliesTo,
  ComplianceProgramCalibrationType,
  ComplianceProgramState,
  GetComplianceProgramTimelinePreviewQuery,
  GetComplianceProgramTimelinePreviewQueryVariables,
  TimelineEntryFragment,
} from "types/graphql-schema";

import getComplianceProgramTimelinePreviewQuery from "@apps/programs/graphql/get-compliance-program-timeline-preview-query";
import useDebounce from "@components/use-debounce/use-debounce";

import { convertProgramFormToMutationVariables } from "./form-data";
import { ProgramForm } from "./types";

/**
 * What the form still has to say before there is anything to plan.
 *
 * `dates` — a single-cycle review is placed on the calendar by its kickoff date,
 * and every step with no offset of its own falls back to the review's due date, so
 * without both of them whole steps drop out of the answer with nothing on screen
 * saying why. An ongoing review is dated from today instead, but it is placed by
 * its two offsets, so it is short of the same thing until it has both.
 *
 * `steps` — a review with nothing turned on sends nothing, so the one row it would
 * come back with says less than a sentence does.
 *
 * `participants` — the same gate the participants preview applies. Resolving a rule
 * that names nobody yet walks the whole organization to answer "nobody", which is
 * an expensive way to say what the form already knows. A published review is never
 * short of it: it is planned against the roster it froze, not against the rule.
 */
export type TimelineGap = "dates" | "steps" | "participants" | null;

/**
 * A rule that means nothing until the admin has named something, and the field the
 * admin names it in.
 *
 * The other rules describe a set on their own. Lives here rather than in the
 * participants preview, which is a component file and can export only components.
 * Both gates read this one copy: two copies that drifted would make the Timeline
 * panel and the participants preview disagree about the same form.
 */
export const missingSelectionField: Partial<
  Record<ComplianceProgramAppliesTo, keyof ProgramForm>
> = {
  [ComplianceProgramAppliesTo.Departments]: "appliesToTeams",
  [ComplianceProgramAppliesTo.ReportsTo]: "appliesToManagers",
  [ComplianceProgramAppliesTo.Users]: "appliesToUsers",
};

const timelineGap = (
  program: ProgramForm,
  { savedRoster }: { savedRoster: boolean },
): TimelineGap => {
  // An ongoing review is dated from today, so it needs no calendar date. It still
  // needs its two offsets: the backend falls back to 0 for a missing one, and a
  // review planned as "0 days after each person's start date" is not the review
  // the admin is in the middle of describing. Zero is missing here, as it is to
  // `validateProgramForm`, because a step opening the day somebody joins is not
  // something the form lets an admin ask for.
  const missingDates = program.ongoing
    ? !program.startDateOffset || !program.startDateDueDateOffset
    : !program.startDate || !program.dueDate;
  if (missingDates) {
    return "dates";
  }
  // Calibration is not a step on the form, but it is one to the backend, so a
  // review with calibration on and no steps still has something to plan.
  if (
    program.steps.length === 0 &&
    program.calibration === ComplianceProgramCalibrationType.Off
  ) {
    return "steps";
  }
  const missingField = missingSelectionField[program.appliesTo];
  if (
    !savedRoster &&
    missingField &&
    (program[missingField] as unknown[]).length === 0
  ) {
    return "participants";
  }
  return null;
};

/**
 * What is still missing, and where the control that fixes it is.
 *
 * `here` is true on the tab that holds the control, false on a tab that only shows
 * the consequence. A panel on the Notifications tab saying "set a date above" would
 * send the admin looking for a date field that is on another tab.
 */
export const timelineGapMessage = (
  gap: Exclude<TimelineGap, null>,
  noun: string,
  { here }: { here: boolean },
) => {
  if (gap === "dates") {
    return here
      ? `Set a kickoff date and a due date above to see what this ${noun} will do.`
      : `Set a kickoff date and a due date on the Workflow tab to see what this ${noun} will send.`;
  }
  if (gap === "steps") {
    return here
      ? "Turn on a step above to see when it opens and what it sends."
      : `Turn on a step on the Workflow tab to see what this ${noun} will send.`;
  }
  return `Choose who takes part on the Participants tab to see what this ${noun} will send.`;
};

export type ProgramFormTimeline = {
  /** The plan, or undefined while there has never been an answer for these values. */
  rows: readonly TimelineEntryFragment[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  /**
   * What the backend refused to answer, in its own words, or null.
   *
   * Only a GraphQL error says something an admin can act on: a rule reaching
   * outside their tree is refused with a sentence naming what to change. A network
   * failure says "Failed to fetch", which is not their problem to fix.
   */
  refusal: string | null;
  /** The message to show instead of a plan, or null when there is one to show. */
  gap: TimelineGap;
  /**
   * How many days after joining a person enters this review, or null.
   *
   * Null for a single-cycle review, which has dates of its own. An ongoing review
   * has none: the backend dates its plan from today so that the plan can say what
   * the review sends, and every reader has to say that the dates below are today's
   * and name the rule they stand in for.
   */
  conditionalKickoffAfterDays: number | null;
};

/**
 * The Timeline of the review this form describes, recomputed as the admin edits.
 *
 * Asks the backend, because the plan is produced by the same simulator the review
 * page reads: which day a step opens, which of the nine automatic notifications
 * each step triggers and who each one leaves out cannot be worked out in the
 * browser, and a panel that guessed differently from the review page would be
 * worse than no panel at all.
 *
 * Both a draft and a review that is already running are planned from the values
 * on the screen, so a date the admin moves shows before it is saved. They differ
 * in who takes part:
 *
 * * a draft resolves its participant rule, because the question there is "what
 *   would this do if I published it now"; and
 * * a running review asks for `savedRoster`, because it froze who takes part when
 *   it was published and the review page reads that frozen roster. Planning it from
 *   the rule would count somebody who joined the department last week, and the form
 *   and the page would disagree about a review neither of them can change.
 *
 * The gaps below are what keep this cheap. Planning a review resolves the whole
 * roster, so a form that has not said when it runs, what it runs, or who is in it
 * is answered from the form itself and never reaches the backend at all.
 *
 * Nothing here saves. The query takes the form's current values as arguments and
 * hands back an answer; the review is written only when the admin saves the form.
 */
const useProgramFormTimeline = (
  proposedComplianceProgram: ProgramForm,
): ProgramFormTimeline => {
  const savedProgramId = proposedComplianceProgram.id;
  const isRunning =
    savedProgramId !== undefined &&
    proposedComplianceProgram.state !== ComplianceProgramState.Draft;

  // The values the query needs, and only those, so that editing a question on
  // another tab does not restart the debounce timer. The title is one of them,
  // unlike in the participants preview, because the kickoff row is named after the
  // review: without it this panel would say "Review starts" where the review page
  // says the review's own name.
  //
  // The gap travels with them, so that the moment an edit closes one the request
  // that goes out carries the edit that closed it rather than the values from
  // before it.
  const live = useMemo(() => {
    // A running review's templates are locked, and the save leaves them out. The
    // conversion rebuilds their settings from the steps, so sending them here would
    // plan settings the review cannot have. Leaving them out keeps the saved ones.
    const saved = convertProgramFormToMutationVariables(
      proposedComplianceProgram,
      !isRunning,
    );
    const variables: GetComplianceProgramTimelinePreviewQueryVariables = {
      organizationId: proposedComplianceProgram.organizationId,
      complianceProgramId: savedProgramId,
      title: saved.title,
      startDate: saved.startDate,
      dueDate: saved.dueDate,
      periodStartDate: saved.periodStartDate,
      periodEndDate: saved.periodEndDate,
      steps: saved.steps,
      assessmentTemplates: saved.assessmentTemplates,
      topicTemplates: saved.topicTemplates,
      calibration: saved.calibration,
      recurrence: saved.recurrence,
      ongoing: saved.ongoing,
      startDateOffset: saved.startDateOffset,
      startDateDueDateOffset: saved.startDateDueDateOffset,
      // The admin's own reminders are part of the plan, so the panel moves the day
      // a reminder lands as soon as its schedule changes.
      scheduledReminders: saved.scheduledReminders,
      appliesTo: saved.appliesTo,
      appliesToTeams: saved.appliesToTeams,
      appliesToManagers: saved.appliesToManagers,
      appliesToUsers: saved.appliesToUsers,
      excludedUsers: saved.excludedUsers,
      excludedHireDateAfter: saved.excludedHireDateAfter,
      // A cleared date and an untouched one both serialize to nothing, so the save
      // conversion's flag says which. Without it the backend keeps planning around
      // the cutoff the draft still has saved.
      removeExcludedHireDateAfter: saved.removeExcludedHireDateAfter,
      savedRoster: isRunning,
    };
    return {
      gap: timelineGap(proposedComplianceProgram, { savedRoster: isRunning }),
      variables,
    };
  }, [proposedComplianceProgram, savedProgramId, isRunning]);

  // Debounced on the values rather than on the object, because every keystroke
  // anywhere in the form hands this hook a new object with the same values.
  const liveKey = JSON.stringify(live);
  const debouncedKey = useDebounce(liveKey, 500);
  const debounced = useMemo(
    () => JSON.parse(debouncedKey) as typeof live,
    [debouncedKey],
  );

  // What is on the screen, which is what the panel says.
  const gap = live.gap;

  const preview = useQuery<
    GetComplianceProgramTimelinePreviewQuery,
    GetComplianceProgramTimelinePreviewQueryVariables
  >(getComplianceProgramTimelinePreviewQuery, {
    fetchPolicy: "cache-and-network",
    variables: debounced.variables,
    skip: debounced.gap !== null,
  });

  const fromServer = preview.data?.complianceProgramTimeline;

  // Apollo drops the data it holds the moment the variables change, so the last
  // answer is kept here: it is what stays on screen while the next one is being
  // fetched.
  const [remembered, setRemembered] = useState<
    readonly TimelineEntryFragment[] | undefined
  >(undefined);
  useEffect(() => {
    if (fromServer) {
      setRemembered(fromServer);
    }
  }, [fromServer]);

  const error = preview.error;

  return {
    // What is on screen stays on screen while the next answer is fetched, and the
    // whole panel swaps at once when it lands. Blanking it on every edit made the
    // plan flash empty each time a date moved, which reads as "this review does
    // nothing" for as long as the request takes. `loading` is what keeps that
    // honest: these rows may describe the form as it was a moment ago, and the
    // panel says so with a spinner rather than by going blank.
    rows: fromServer ?? remembered,
    loading:
      preview.loading ||
      // An edit the debounce has not carried to the server yet is work in
      // progress. Saying so keeps the panel from reading a gap it has just closed
      // as a review that does nothing.
      (gap === null && debouncedKey !== liveKey),
    error,
    refusal: error?.graphQLErrors?.[0]?.message ?? null,
    gap,
    conditionalKickoffAfterDays: proposedComplianceProgram.ongoing
      ? (proposedComplianceProgram.startDateOffset ?? 0)
      : null,
  };
};

export default useProgramFormTimeline;
